import React, { useContext, useEffect, useState } from "react";
import "./Dashboard.css";
import { Button, Col, Row, Input } from "antd";
import axios from "axios";
import * as links from "../../../utils/links";
import { AuthContext } from "../../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import LoadingAction from "../../../themes/LoadingAction/LoadingAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCheckCircle,
  faTrashCan,
  faUserPlus,
  faUsers,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const toNumberMoney = (raw) => {
  if (raw === null || raw === undefined) return 0;
  let s = String(raw).trim();
  if (!s) return 0;
  s = s.replace(/^R\$\s*/i, "").replace(/\s/g, "");
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    const dots = s.match(/\./g) || [];
    if (dots.length > 1) s = s.replace(/\./g, "");
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtBRL = (value) => {
  const n = Number(value || 0);
  try {
    return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (e) {
    return String(n.toFixed(2));
  }
};

const parseJsonMaybe = (value) => {
  if (typeof value !== "string") return value;
  const s = value.trim();
  if (!s) return value;
  try {
    return JSON.parse(s);
  } catch (_) {
    return value;
  }
};

const normalizeBonusMetodos = (raw) => {
  const v = parseJsonMaybe(raw);
  if (Array.isArray(v)) {
    return v
      .map((item) => (typeof item === "string" ? item : item?.value ?? item?.key ?? item?.metodo ?? item?.tipo))
      .filter(Boolean);
  }
  return [];
};

const normalizeBonusRegras = (raw) => {
  const v0 = parseJsonMaybe(raw);
  const v =
    v0 && typeof v0 === "object" && !Array.isArray(v0)
      ? v0.regras ?? v0.rules ?? v0.bonusRegras ?? v0
      : v0;

  if (!Array.isArray(v)) return [];

  return v
    .map((item) => {
      const it = parseJsonMaybe(item);
      if (!it || typeof it !== "object") return null;

      const valorMinimo =
        Number(it.valorMinimo ?? it.valor_minimo ?? it.valor ?? it.min ?? it.minimo ?? it.valueMin ?? it.value) || 0;
      const bonus = Number(it.bonus ?? it.bonus_pulsos ?? it.extra ?? it.extraPulsos ?? it.bonusValor) || 0;

      return { valorMinimo, bonus };
    })
    .filter(Boolean)
    .sort((a, b) => a.valorMinimo - b.valorMinimo);
};

const getReceptionIcon = (nivelDeSinal) => {
  const nivel = Number(nivelDeSinal || 0);
  if (!nivel || Number.isNaN(nivel)) return 0;
  if (nivel <= 30) return 1;
  if (nivel <= 60) return 2;
  if (nivel <= 70) return 3;
  return 4;
};

const initialDataSignIn = {
  nome: "",
  cpf: "",
  senha: "",
  confirmaSenha: ""
};

const initErrorField = {
  nome: undefined,
  cpf: undefined,
  senha: undefined,
  confirmaSenha: undefined
};

const DashboardFornecedor = () => {
  const { setDataUser, authInfo, setNotiMessage } =
    useContext(AuthContext);

  const { dataUser } = authInfo;
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [funcionaraioModal, setFuncionarioModal] = useState(false);
  const [mostrarFuncionariosLiberados, setMostrarFuncionariosLiberados] = useState(false);
  const [funcionariosLiberados, setFuncionariosLiberados] = useState([]);

  const [dataAuth, setDataSingUp] = useState({ ...initialDataSignIn });
  const [errorField, setErrorField] = useState({ ...initErrorField });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const getFuncionariosStorageKey = () =>
    `ikpay_funcionarios_liberados_${authInfo?.dataUser?.clienteId || "default"}`;

  const normalizarCpf = (value) => String(value || "").replace(/\D/g, "");

  const normalizarFuncionario = (item, index = 0) => {
    const cpf = normalizarCpf(item?.cpf || item?.documento || item?.username || "");
    return {
      id: item?.id || item?._id || `${cpf || "funcionario"}-${index}`,
      nome: item?.nome || item?.name || item?.usuario || item?.username || "Sem nome",
      cpf,
      criadoEm: item?.criadoEm || item?.createdAt || item?.dataInclusao || new Date().toISOString(),
    };
  };

  const salvarFuncionariosLocal = (lista) => {
    const normalizados = (Array.isArray(lista) ? lista : []).map(normalizarFuncionario);
    setFuncionariosLiberados(normalizados);
    try {
      window.localStorage.setItem(getFuncionariosStorageKey(), JSON.stringify(normalizados));
    } catch (_) { }
  };

  const carregarFuncionariosLocal = () => {
    try {
      const raw = window.localStorage.getItem(getFuncionariosStorageKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(normalizarFuncionario) : [];
    } catch (_) {
      return [];
    }
  };

  const mesclarFuncionarios = (listaBase, listaNova) => {
    const map = new Map();
    [...(Array.isArray(listaBase) ? listaBase : []), ...(Array.isArray(listaNova) ? listaNova : [])]
      .map(normalizarFuncionario)
      .forEach((item) => {
        const key = item.cpf || item.id || item.nome;
        if (!key) return;
        map.set(key, item);
      });
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.criadoEm || 0).getTime() - new Date(a.criadoEm || 0).getTime()
    );
  };

  const buscarFuncionariosLiberados = async () => {
    const listaLocal = carregarFuncionariosLocal();
    setFuncionariosLiberados(listaLocal);

    const endpoints = [
      `${process.env.REACT_APP_SERVIDOR}/funcionarios?clienteId=${authInfo?.dataUser?.clienteId}`,
      `${process.env.REACT_APP_SERVIDOR}/funcionario?clienteId=${authInfo?.dataUser?.clienteId}`,
    ];

    for (const endpoint of endpoints) {
      try {
        const res = await axios.get(endpoint, {
          headers: {
            "x-access-token": token,
          },
        });

        if (Array.isArray(res?.data)) {
          const listaFinal = mesclarFuncionarios(listaLocal, res.data);
          salvarFuncionariosLocal(listaFinal);
          return;
        }
      } catch (_) { }
    }
  };

  const removerFuncionarioLiberado = async (funcionario) => {
    const confirmacao = await Swal.fire({
      icon: "warning",
      title: "Remover acesso",
      text: `Deseja remover o acesso de ${funcionario?.nome || "este funcionário"}?`,
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    const listaAtual = carregarFuncionariosLocal();
    const listaAtualizada = listaAtual.filter((item) => {
      const mesmoCpf = funcionario?.cpf && item?.cpf && item.cpf === funcionario.cpf;
      const mesmoId = funcionario?.id && item?.id && item.id === funcionario.id;
      return !(mesmoCpf || mesmoId);
    });

    const endpoints = [
      `${process.env.REACT_APP_SERVIDOR}/funcionario/${funcionario?.id}`,
      `${process.env.REACT_APP_SERVIDOR}/funcionarios/${funcionario?.id}`,
    ];

    const requestConfig = {
      headers: {
        "x-access-token": token,
      },
      params: {
        clienteId: authInfo?.dataUser?.clienteId,
      },
      data: {
        clienteId: authInfo?.dataUser?.clienteId,
        cpf: funcionario?.cpf,
      },
    };

    let backendRemoveu = !funcionario?.id;
    let ultimoErro = null;
    for (const endpoint of endpoints) {
      if (!funcionario?.id) break;
      try {
        await axios.delete(endpoint, requestConfig);
        backendRemoveu = true;
        break;
      } catch (error) {
        ultimoErro = error;
      }
    }

    if (!backendRemoveu) {
      setNotiMessage({
        type: "error",
        message:
          ultimoErro?.response?.data?.error ||
          "Não foi possível remover o acesso do funcionário no servidor.",
      });
      return;
    }

    salvarFuncionariosLocal(listaAtualizada);

    setNotiMessage({
      type: "success",
      message: backendRemoveu
        ? "Acesso do funcionário removido com sucesso."
        : "Funcionário removido da lista local de acessos liberados.",
    });
  };

  const gerarPdfBonus = async (maquinaResumo) => {
    const maquinaId = maquinaResumo?.id;
    let maquina = maquinaResumo;

    if (maquinaId) {
      try {
        const res = await axios.get(`${process.env.REACT_APP_SERVIDOR}/maquina/${maquinaId}`, {
          headers: {
            "x-access-token": token,
          },
        });
        maquina = res.data || maquinaResumo;
      } catch (e) { }
    }

    if (!maquina?.bonusAtivo) {
      const caminho = `${links.EDIT_FORNECEDOR_CANAIS}/${maquinaId}`;

      const r = await Swal.fire({
        icon: "error",
        title: "Bônus não está ativo",
        html: `<div style="text-align:left">
          <div style="margin-bottom:8px">Ative a jogada bônus para conseguir exportar o PDF.</div>
          <div style="margin-bottom:8px"><b>Caminho:</b> ${caminho}</div>
          <div>Dentro da tela: <b>Configuração de Bônus</b> → <b>Ativar Jogada Bônus</b>.</div>
        </div>`,
        showCancelButton: true,
        confirmButtonText: "Abrir tela",
        cancelButtonText: "Fechar",
      });

      if (r.isConfirmed) {
        navigate(caminho);
      }

      return;
    }

    const regras = normalizeBonusRegras(maquina?.bonusRegras);
    const metodos = normalizeBonusMetodos(maquina?.bonusMetodos);

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "600px";
    container.style.padding = "40px";
    container.style.textAlign = "center";
    container.style.fontFamily = "Arial, sans-serif";
    container.style.border = "2px solid #1890ff";
    container.style.borderRadius = "15px";
    container.style.backgroundColor = "#fff";
    container.style.color = "#111";

    const regrasHtml = regras
      .map((regra) => {
        const valorMinimo = Number(regra?.valorMinimo) || 0;
        const bonus = Number(regra?.bonus) || 0;
        const jogadasNormais = Math.floor(valorMinimo / 2);
        const jogadasBonus = Math.floor(bonus / 2);
        const totalJogadas = jogadasNormais + jogadasBonus;

        return `
          <div style="
            background-color:#f0f2f5;
            padding:20px;
            border-radius:10px;
            display:flex;
            justify-content:space-between;
            align-items:center;
            border:1px solid #d9d9d9;
          ">
            <div style="text-align:left">
              <span style="font-size:24px;font-weight:bold;color:#111">R$ ${fmtBRL(valorMinimo)}</span>
            </div>
            <div style="text-align:right">
              <span style="font-size:20px;color:#1890ff;font-weight:bold">
                ${jogadasNormais} + ${jogadasBonus} BÔNUS = ${totalJogadas} JOGADAS!
              </span>
            </div>
          </div>
        `;
      })
      .join("");

    const metodoLabels = {
      PIX: "Pix",
      CREDITO: "Crédito",
      DEBITO: "Débito",
      ESPECIE: "Espécie",
    };
    const metodosTexto = metodos.length
      ? metodos.map((m) => metodoLabels[m] || m).join(" • ")
      : "Nenhum";

    container.innerHTML = `
      <h1 style="color:#1890ff;font-size:32px;margin-bottom:10px">JOGADA BÔNUS ATIVA! 🚀</h1>
      <p style="font-size:18px;font-weight:bold;margin-bottom:20px">
        💳 Métodos: ${metodosTexto}
      </p>
      <p style="font-size:18px;color:#666;margin-bottom:30px">
        Cada jogada custa R$ 2,00. Aproveite os bônus abaixo:
      </p>
      <div style="display:flex;flex-direction:column;gap:15px">
        ${regrasHtml}
      </div>
      <p style="margin-top:40px;font-size:14px;color:#333;font-weight:bold">
        Obs: em espécie tem que ser nota única (ex: 1 nota de R$ 20,00) conforme bônus a cima.
      </p>
      <div style="
        margin-top:30px;
        padding-top:20px;
        border-top:1px solid #eee;
        display:flex;
        flex-direction:column;
        align-items:center;
        gap:5px;
      ">
        <div style="font-size:18px;font-weight:bold;color:#000000;letter-spacing:1px">
          Oferecimento: IkPay sistema
        </div>
      </div>
    `;

    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 10, pdfWidth, pdfHeight);
      pdf.save("bonus.pdf");
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao gerar o PDF de bônus.",
      });
    } finally {
      document.body.removeChild(container);
    }
  };

  const buscarMaquinas = ({ silent } = { silent: false }) => {
    if (!silent) setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);

        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas({ silent: false });
    buscarFuncionariosLiberados();

    const interval = setInterval(() => {
      buscarMaquinas({ silent: true });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = (post) => {
    navigate(`${links.FORNECEDOR_SEARCH_CANAIS}/${post.id}`, { state: post });
  };


  const cadastraFuncionario = () => {
    let dataErrorField = {};
    if (dataAuth.nome.trim() === "") {
      dataErrorField = {
        ...dataErrorField,
        nome: "Nome obrigatório.",
      };
    } else if (dataAuth.cpf.trim() === "" || dataAuth.cpf.length < 11) {
      dataErrorField = {
        ...dataErrorField,
        cpf: "CPF deve conter 11 digitos.",
      };
    }
    if (dataAuth.senha.trim() === "") {
      dataErrorField = {
        ...dataErrorField,
        senha: "Senha obrigatória",
      };
    } else if (dataAuth.senha.trim().length < 6) {
      dataErrorField = {
        ...dataErrorField,
        senha: "Senha tem que ter no mínimo 6 dígitos.",
      };
    }
    if (dataAuth.confirmaSenha.trim() === "") {
      dataErrorField = {
        ...dataErrorField,
        confirmaSenha: "Confirme a senha obrigatória",
      };
    } else if (dataAuth.confirmaSenha.trim().length < 6) {
      dataErrorField = {
        ...dataErrorField,
        confirmaSenha: "Senha tem que ter no mínimo 6 dígitos.",
      };
    } else if (dataAuth.senha != dataAuth.confirmaSenha) {
      dataErrorField = {
        ...dataErrorField,
        confirmaSenha: "As senhas não são iguais",
      };
    }

    setSuccess(false);
    if (Object.keys(dataErrorField).length === 0) {
      setIsLoading(true);
      setError(null);
      axios.post(`${process.env.REACT_APP_SERVIDOR}/funcionario`, {
        nome: dataAuth.nome,
        senha: dataAuth.senha,
        cpf: dataAuth.cpf.replace(/\D/g, ""),
      }, {
        headers: {
          "x-access-token": token,
        },
      }).then((res) => {
        if (res.status === 200) {
          const funcionarioCriado = normalizarFuncionario({
            ...(res?.data || {}),
            nome: dataAuth.nome,
            cpf: dataAuth.cpf,
            criadoEm: new Date().toISOString(),
          });
          salvarFuncionariosLocal(mesclarFuncionarios(carregarFuncionariosLocal(), [funcionarioCriado]));
          setNotiMessage({
            type: 'success',
            message: 'Funcionário cadastrado com sucesso!'
          })
          setIsLoading(false);
          setSuccess(true);
          setDataSingUp({
            ...initialDataSignIn,
          });
          setDataUser({
            ...res.data,
          });
          setFuncionarioModal(false)
        } else {
          throw new Error();
        }
      })
        .catch((err) => {
          setIsLoading(false);
          if (err.response.status === 500) {
            setError(
              'Usuário já existe, <a target="_blank" href=' +
              links.FORGOTPASSWORD +
              ">esqueceu sua senha<a/>?"
            );
          } else {
            setError(
              "Erro, algo deu errado " + (err.response?.data?.error ?? "")
            );
          }
        });
    } else {
      setErrorField((prev) => {
        return {
          ...prev,
          ...dataErrorField,
        };
      });
    }
  };

  return (
    <div className="Dashboard_container">


      {funcionaraioModal && (
        <div
          className="IkLanding_loginOverlay"
          onClick={() => {
            setFuncionarioModal(false);

          }}
          role="button"
          tabIndex={-1}
        >
          <div
            className="IkLanding_loginModal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Cadastro do novo funcionário"
          >
            <div className="IkLanding_loginModalHeader">
              <div className="IkLanding_loginModalTitle">Novo funcionário</div>
              <button className="IkLanding_loginClose" onClick={() => setFuncionarioModal(false)} type="button">
                ×
              </button>
            </div>

            <div className="IkLanding_loginField">
              <div className="IkLanding_loginLabel">Nome</div>
              <Input
                placeholder="Digite o nome do funcionário"
                value={dataAuth?.nome ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setDataSingUp((prev) => ({ ...prev, nome: value }));
                  setErrorField((prev) => ({ ...prev, nome: undefined }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onsubmit();
                }}
              />
              {!!errorField?.nome && <div className="IkLanding_loginFieldError">{errorField.nome}</div>}
            </div>

            <div className="IkLanding_loginField">
              <div className="IkLanding_loginLabel">CPF</div>
              <Input
                placeholder="000.000.000-00"
                type="number"
                value={dataAuth?.cpf ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setDataSingUp((prev) => ({ ...prev, cpf: value }));
                  setErrorField((prev) => ({ ...prev, cpf: undefined }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onsubmit();
                }}
              />
              {!!errorField?.cpf && <div className="IkLanding_loginFieldError">{errorField.cpf}</div>}
            </div>

            <div className="IkLanding_loginField">
              <div className="IkLanding_loginLabel">Senha</div>
              <Input
                placeholder="Digite a senha do funcionário"
                value={dataAuth?.senha ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setDataSingUp((prev) => ({ ...prev, senha: value }));
                  setErrorField((prev) => ({ ...prev, senha: undefined }));
                }}
              />
              {!!errorField?.senha && <div className="IkLanding_loginFieldError">{errorField.senha}</div>}
            </div>
            <div className="IkLanding_loginField">
              <div className="IkLanding_loginLabel">Confirma Senha</div>
              <Input
                placeholder="Confirme a senha"
                value={dataAuth?.confirmaSenha ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setDataSingUp((prev) => ({ ...prev, confirmaSenha: value }));
                  setErrorField((prev) => ({ ...prev, confirmaSenha: undefined }));
                }}
              />
              {!!errorField?.confirmaSenha && <div className="IkLanding_loginFieldError">{errorField.confirmaSenha}</div>}
            </div>

            <Button type="primary" block className="IkLanding_loginSubmit" onClick={() => cadastraFuncionario()}>
              Cadastrar funcionário
            </Button>
          </div>
        </div>
      )}

      {isLoading && <LoadingAction />}

      <div className="Dashboard_header">
        <h2>Monitoramento</h2>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Button onClick={buscarMaquinas}>
          <FontAwesomeIcon icon={faArrowsRotate} /> Atualizar
        </Button>
        {authInfo?.dataUser.key !== "FUNCIONARIO" ?
          <>
            <Button
              type="primary"
              onClick={() => {
                setFuncionarioModal(true)
              }}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Liberar acesso para funcionários
            </Button>
            <Button
              onClick={() => {
                const proximoEstado = !mostrarFuncionariosLiberados;
                setMostrarFuncionariosLiberados(proximoEstado);
                if (proximoEstado) {
                  buscarFuncionariosLiberados();
                }
              }}
            >
              <FontAwesomeIcon icon={faUsers} /> Funcionários liberados
            </Button>
          </>
          :
          null
        }

      </div>

      {authInfo?.dataUser.key !== "FUNCIONARIO" && mostrarFuncionariosLiberados && (
        <div
          style={{
            marginTop: 16,
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div style={{ color: "rgba(233, 242, 255, 0.95)", fontWeight: 700, fontSize: 16 }}>
              Funcionários liberados
            </div>
            <Button size="small" onClick={buscarFuncionariosLiberados}>
              Atualizar lista
            </Button>
          </div>

          {funcionariosLiberados.length === 0 ? (
            <div style={{ color: "rgba(233, 242, 255, 0.68)" }}>
              Nenhum funcionário liberado encontrado ainda.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {funcionariosLiberados.map((funcionario) => (
                <div
                  key={funcionario.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: 10,
                    padding: "12px 14px",
                  }}
                >
                  <div>
                    <div style={{ color: "rgba(233, 242, 255, 0.95)", fontWeight: 700 }}>
                      {funcionario.nome}
                    </div>
                    <div style={{ color: "rgba(233, 242, 255, 0.68)", fontSize: 13 }}>
                      CPF: {funcionario.cpf || "-"}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ color: "rgba(233, 242, 255, 0.6)", fontSize: 12 }}>
                      Liberado em{" "}
                      {funcionario.criadoEm
                        ? new Date(funcionario.criadoEm).toLocaleDateString("pt-BR")
                        : "-"}
                    </div>
                    <Button
                      danger
                      size="small"
                      onClick={() => removerFuncionarioLiberado(funcionario)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} /> Remover acesso
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        {maquinas.map((post) => {
          const isOnline =
            post.status === "ONLINE" ||
            post.status === "PAGAMENTO_RECENTE";

          const pagamentoRecente =
            post.status === "PAGAMENTO_RECENTE";

          const receptionIcon = getReceptionIcon(post.nivelDeSinal);
          const receptionColor = pagamentoRecente ? "#fff" : "rgba(0, 210, 255, 0.95)";
          const titleColor = pagamentoRecente ? "rgba(255, 255, 255, 0.98)" : "rgba(233, 242, 255, 0.98)";
          const titleShadow = "0 1px 2px rgba(0,0,0,0.35)";
          const labelColor = pagamentoRecente ? "rgba(255, 255, 255, 0.92)" : "rgba(233, 242, 255, 0.75)";
          const moneyColor = "#22c55e";
          const moneyShadow = "0 1px 2px rgba(0,0,0,0.35)";
          const actionHeight = 28;
          const actionFontSize = 12;

          return (
            <Col key={post.id} xs={24} md={12} lg={8}>
              <div
                onClick={() => handleClick(post)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  background: pagamentoRecente ? "var(--ik-gradient)" : "rgba(255, 255, 255, 0.06)",
                  color: pagamentoRecente ? "rgba(255, 255, 255, 0.96)" : "rgba(233, 242, 255, 0.92)",
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: pagamentoRecente
                    ? "0 0 20px rgba(22,119,255,0.5)"
                    : "0 10px 25px rgba(0,0,0,0.25)",
                  border: pagamentoRecente ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(255,255,255,0.12)",
                  borderLeft: `6px solid ${isOnline ? "#22c55e" : "#ef4444"
                    }`,
                  cursor: "pointer",
                }}
              >
                {/* TOPO */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <h3 style={{ margin: 0, color: titleColor, textShadow: titleShadow, fontWeight: 700 }}>
                    {post.nome}
                  </h3>

                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div
                      style={{
                        height: actionHeight,
                        padding: "0 10px",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: actionFontSize,
                        lineHeight: `${actionFontSize}px`,
                        fontWeight: 600,
                        background: pagamentoRecente ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                        color: post.status === "OFFLINE" ? "#ef4444" : "#22c55e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={post.status === "OFFLINE" ? faXmarkCircle : faCheckCircle}
                        style={{ fontSize: actionFontSize }}
                      />
                      {post.status === "OFFLINE" ? "Offline" : "Online"}
                    </div>

                    <div
                      style={{
                        height: actionHeight,
                        padding: "0 10px",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: actionFontSize,
                        lineHeight: `${actionFontSize}px`,
                        fontWeight: 600,
                        background: pagamentoRecente ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                        color: receptionColor,
                        whiteSpace: "nowrap",
                      }}
                      title={`${post?.nivelDeSinal ? `${post.nivelDeSinal}%` : "0%"}`}
                    >
                      <i className={`bi bi-reception-${receptionIcon}`} style={{ fontSize: actionFontSize }}></i>
                      {post?.nivelDeSinal ? `${post.nivelDeSinal}%` : "0%"}
                    </div>
                  </div>
                </div>

                {/* FATURAMENTO */}
                <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <small style={{ color: labelColor }}>Faturamento hoje</small>
                    <h2 style={{ margin: 0, color: moneyColor, textShadow: moneyShadow }}>
                      R$ {fmtBRL(toNumberMoney(post.faturamentoHoje))}
                    </h2>
                    <small style={{ color: labelColor }}>Saídas hoje: {Number(post?.saidasHoje || 0)}</small>
                  </div>
                  <div style={{ flex: 1, textAlign: "right" }}>
                    <small style={{ color: labelColor }}>Faturamento ontem</small>
                    <h2 style={{ margin: 0, color: moneyColor, textShadow: moneyShadow }}>
                      R$ {fmtBRL(toNumberMoney(post.faturamentoOntem))}
                    </h2>
                  </div>
                </div>

                {/* INFO */}
                <div style={{ marginTop: 10 }}>
                  <div>Máquina: {post.store_id}</div>
                  <div>Serial: {post.maquininha_serial}</div>
                  <div>Descrição: {post?.descricao || "-"}</div>
                </div>

                {/* BOTÕES */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    marginTop: "auto",
                    paddingTop: 14,
                  }}
                >
                  {authInfo?.dataUser.key != 'FUNCIONARIO' ?
                    <button
                      onClick={(e) => {
                        e.stopPropagation();

                        Swal.fire({
                          title: '💰 Digite o valor',
                          input: 'number',
                          inputLabel: 'Valor do crédito (R$)',
                          inputValue: post.valorDoPix || "1.00",
                          customClass: {
                            popup: "ikpay-swal-dark",
                          },
                          showCancelButton: true,
                          confirmButtonText: 'Enviar Crédito',
                          cancelButtonText: 'Cancelar',
                          confirmButtonColor: '#00d2ff',
                          inputValidator: (value) => {
                            if (!value) {
                              return 'Você precisa digitar um valor!'
                            }
                          }
                        }).then((result) => {
                          if (result.isConfirmed) {
                            const valorFinal = result.value;

                            axios.post(
                              `${process.env.REACT_APP_SERVIDOR}/credito-remoto-cliente`,
                              {
                                id: post.id,
                                valor: valorFinal
                              },
                              {
                                headers: {
                                  "x-access-token": token,
                                },
                              }
                            ).then(() => {
                              Swal.fire({
                                icon: 'success',
                                title: 'Sucesso',
                                text: 'Crédito remoto enviado com sucesso!',
                                timer: 2000,
                                showConfirmButton: false
                              });
                            }).catch(err => {
                              Swal.fire({
                                icon: 'error',
                                title: 'Erro',
                                text: err.response?.data?.msg || 'Falha ao enviar crédito remoto.',
                              });
                            });
                          }
                        });
                      }}
                      style={{
                        flex: 1,
                        background: isOnline ? "#22c55e" : "#ef4444",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 8px",
                        height: actionHeight,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        fontSize: actionFontSize,
                        lineHeight: `${actionFontSize}px`,
                      }}
                    >
                      <i className="bi bi-credit-card-2-front"></i> Crédito Remoto
                    </button>
                    :
                    null
                  }


                  <button
                    onClick={(e) => {
                      e.stopPropagation();

                      Swal.fire({
                        title: '🔗 Gerar Link de Liberação',
                        input: 'number',
                        inputLabel: 'Valor do link (R$)',
                        inputValue: post.valorDoPix || "1.00",
                        showCancelButton: true,
                        confirmButtonText: 'Gerar Link',
                        cancelButtonText: 'Cancelar',
                        confirmButtonColor: '#00d2ff',
                        inputValidator: (value) => {
                          if (!value) {
                            return 'Você precisa digitar um valor!'
                          }
                        }
                      }).then((result) => {
                        if (result.isConfirmed) {
                          const valorFinal = result.value;

                          axios
                            .post(
                              `${process.env.REACT_APP_SERVIDOR}/gerar-link`,
                              {
                                maquinaId: post.id,
                                valor: valorFinal
                              },
                              {
                                headers: {
                                  "x-access-token": token,
                                },
                              }
                            )
                            .then((res) => {
                              if (res.data.link) {
                                const linkGerado = res.data.link;

                                Swal.fire({
                                  title: '🔗 Link Gerado com Sucesso',
                                  html: `
                                    <p style="color:#666;margin:0 0 12px;">Copie e envie para o cliente</p>
                                    <input id="link-gerado" class="swal2-input" value="${linkGerado}" readonly />
                                  `,
                                  showCancelButton: true,
                                  confirmButtonText: '📋 Copiar Link',
                                  cancelButtonText: 'Fechar',
                                  confirmButtonColor: '#00d2ff',
                                }).then(async (copyResult) => {
                                  if (!copyResult.isConfirmed) return;

                                  const el = document.getElementById('link-gerado');
                                  const texto = el?.value || linkGerado;

                                  try {
                                    await navigator.clipboard.writeText(texto);
                                    Swal.fire({
                                      icon: 'success',
                                      title: 'Link copiado!',
                                      timer: 1500,
                                      showConfirmButton: false,
                                    });
                                  } catch (e) {
                                    const textarea = document.createElement('textarea');
                                    textarea.value = texto;
                                    textarea.style.position = 'fixed';
                                    textarea.style.left = '-9999px';
                                    document.body.appendChild(textarea);
                                    textarea.focus();
                                    textarea.select();
                                    const ok = document.execCommand('copy');
                                    document.body.removeChild(textarea);

                                    if (ok) {
                                      Swal.fire({
                                        icon: 'success',
                                        title: 'Link copiado!',
                                        timer: 1500,
                                        showConfirmButton: false,
                                      });
                                    } else {
                                      Swal.fire({
                                        icon: 'error',
                                        title: 'Erro',
                                        text: 'Não foi possível copiar o link.',
                                      });
                                    }
                                  }
                                });
                              } else {
                                Swal.fire({
                                  icon: 'error',
                                  title: 'Erro',
                                  text: 'Link não gerado pelo servidor.',
                                });
                              }
                            })
                            .catch(err => {
                              Swal.fire({
                                icon: 'error',
                                title: 'Erro',
                                text: 'Falha ao gerar link.',
                              });
                            });
                        }
                      });
                    }}
                    style={{
                      flex: 1,
                      background: isOnline ? "#22c55e" : "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 8px",
                      height: actionHeight,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      fontSize: actionFontSize,
                      lineHeight: `${actionFontSize}px`,
                    }}
                  >
                    <i className="bi bi-link-45deg"></i> Link
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      gerarPdfBonus(post);
                    }}
                    style={{
                      flex: "1 1 140px",
                      background: isOnline ? "#22c55e" : "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 8px",
                      height: actionHeight,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      fontSize: actionFontSize,
                      lineHeight: `${actionFontSize}px`,
                    }}
                  >
                    <i className="bi bi-file-earmark-pdf"></i> PDF Bônus
                  </button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default DashboardFornecedor;

export const DashboardMaquinasPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const buscarMaquinas = ({ silent } = { silent: false }) => {
    if (!silent) setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    console.log(token)
    buscarMaquinas({ silent: false });
    const interval = setInterval(() => {
      buscarMaquinas({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const faturamentoHojeTotal = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoHoje), 0);
    const faturamentoOntemTotal = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoOntem), 0);
    const onlineCount = list.filter((m) => m?.status === "ONLINE" || m?.status === "PAGAMENTO_RECENTE").length;
    const pagamentoRecenteCount = list.filter((m) => m?.status === "PAGAMENTO_RECENTE").length;
    const sinalList = list.map((m) => Number(m?.nivelDeSinal || 0)).filter((n) => Number.isFinite(n) && n > 0);
    const sinalMedio = sinalList.length ? Math.round(sinalList.reduce((a, b) => a + b, 0) / sinalList.length) : 0;
    return {
      faturamentoHojeTotal,
      faturamentoOntemTotal,
      onlineCount,
      pagamentoRecenteCount,
      sinalMedio,
      totalMaquinas: list.length,
    };
  })();

  const displayDate = (() => {
    try {
      const d = new Date();
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
    } catch (e) {
      return "";
    }
  })();

  const handleClick = (post) => {
    Swal.fire({
      icon: "info",
      title: "Detalhar máquina",
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const sortedMaquinas = (() => {
    const list = Array.isArray(maquinas) ? [...maquinas] : [];
    list.sort((a, b) => {
      const aHoje = toNumberMoney(a?.faturamentoHoje);
      const bHoje = toNumberMoney(b?.faturamentoHoje);
      if (bHoje !== aHoje) return bHoje - aHoje;
      const aSignal = Number(a?.nivelDeSinal || 0);
      const bSignal = Number(b?.nivelDeSinal || 0);
      return bSignal - aSignal;
    });
    return list.slice(0, 7);
  })();

  const oportunidadePrincipal = (() => {
    const offline = (Array.isArray(maquinas) ? maquinas : []).filter((m) => m?.status === "OFFLINE").length;
    const baixoSinal = (Array.isArray(maquinas) ? maquinas : []).filter((m) => Number(m?.nivelDeSinal || 0) > 0 && Number(m?.nivelDeSinal || 0) <= 30).length;
    if (offline > 0) {
      return {
        title: "Revise máquinas offline",
        desc: "Máquinas offline param de faturar. Verifique energia, internet e sinal.",
        badge: "Alta prioridade",
        impact: `+${offline} máquina(s)`,
      };
    }
    if (baixoSinal > 0) {
      return {
        title: "Melhore o sinal de rede",
        desc: "Sinal baixo pode causar falhas de comunicação e queda de conversão.",
        badge: "Oportunidade",
        impact: `+${baixoSinal} máquina(s)`,
      };
    }
    return {
      title: "Acompanhe as melhores máquinas",
      desc: "Veja as máquinas com melhor faturamento e replique a estratégia de ponto.",
      badge: "Sugestão",
      impact: "+performance",
    };
  })();

  return (
    <div className="Dashboard_container DashboardPremium_container">
      {isLoading && <LoadingAction />}

      <div className="DashboardPremium_top">
        <div>
          <div className="DashboardPremium_hi">Olá, {dataUser?.name || "cliente"}!</div>
          <div className="DashboardPremium_sub">Aqui está o resumo do desempenho das suas máquinas.</div>
        </div>
        <div className="DashboardPremium_date">{displayDate}</div>
      </div>

      <div className="DashboardPremium_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento hoje</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">$</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoHojeTotal)}</div>
          <div className="DashboardPremium_statHint">Ontem: R$ {fmtBRL(resumo.faturamentoOntemTotal)}</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Máquinas online</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">✓</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.onlineCount}/{resumo.totalMaquinas}</div>
          <div className="DashboardPremium_statHint">Status ativo no monitoramento</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Pagamento recente</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">⏱</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.pagamentoRecenteCount}</div>
          <div className="DashboardPremium_statHint">Últimas atualizações</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Sinal médio</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">⌁</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.sinalMedio}%</div>
          <div className="DashboardPremium_statHint">Média de recepção</div>
        </div>
      </div>

      <div className="DashboardPremium_grid">
        <div className="DashboardPremium_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Desempenho das máquinas</div>
            <button
              type="button"
              className="DashboardPremium_linkBtn"
              onClick={() => navigate(links.DASHBOARD_FORNECEDOR)}
            >
              Ver básico
            </button>
          </div>

          <div className="DashboardPremium_table">
            <div className="DashboardPremium_tableHead">
              <div>Máquina</div>
              <div className="DashboardPremium_tableHideSm">Local</div>
              <div>Faturamento hoje</div>
              <div className="DashboardPremium_tableHideSm">Ontem</div>
              <div>Status</div>
            </div>

            {sortedMaquinas.map((m) => {
              const isOnline = m?.status === "ONLINE" || m?.status === "PAGAMENTO_RECENTE";
              const pagamentoRecente = m?.status === "PAGAMENTO_RECENTE";
              const statusLabel = m?.status === "OFFLINE" ? "Offline" : pagamentoRecente ? "Pagamento recente" : "Online";
              const statusClass = m?.status === "OFFLINE" ? "DashboardPremium_badgeRed" : pagamentoRecente ? "DashboardPremium_badgeBlue" : "DashboardPremium_badgeGreen";
              const receptionIcon = getReceptionIcon(m?.nivelDeSinal);
              return (
                <button
                  key={m.id}
                  type="button"
                  className="DashboardPremium_row"
                  onClick={() => handleClick(m)}
                >
                  <div className="DashboardPremium_rowMachine">
                    <div className="DashboardPremium_machineName">{m.nome}</div>
                    <div className="DashboardPremium_machineMeta">
                      <span className={isOnline ? "DashboardPremium_dotOn" : "DashboardPremium_dotOff"}></span>
                      <span>{m.store_id || "-"}</span>
                      <span className="DashboardPremium_signal">
                        <i className={`bi bi-reception-${receptionIcon}`}></i> {m?.nivelDeSinal ? `${m.nivelDeSinal}%` : "0%"}
                      </span>
                    </div>
                  </div>
                  <div className="DashboardPremium_tableHideSm">{m.store_id || "-"}</div>
                  <div className="DashboardPremium_money">R$ {fmtBRL(toNumberMoney(m?.faturamentoHoje))}</div>
                  <div className="DashboardPremium_tableHideSm DashboardPremium_money">R$ {fmtBRL(toNumberMoney(m?.faturamentoOntem))}</div>
                  <div>
                    <span className={`DashboardPremium_badge ${statusClass}`}>{statusLabel}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="DashboardPremium_side">
          <div className="DashboardPremium_card">
            <div className="DashboardPremium_cardHeader">
              <div className="DashboardPremium_cardTitle">Oportunidades para faturar mais</div>
            </div>

            <div className="DashboardPremium_opportunity">
              <div className="DashboardPremium_opTop">
                <div className="DashboardPremium_opTitle">{oportunidadePrincipal.title}</div>
                <span className="DashboardPremium_chip">{oportunidadePrincipal.badge}</span>
              </div>
              <div className="DashboardPremium_opDesc">{oportunidadePrincipal.desc}</div>
              <div className="DashboardPremium_opImpact">
                Impacto estimado: <span className="DashboardPremium_opImpactValue">{oportunidadePrincipal.impact}</span>
              </div>
              <button type="button" className="DashboardPremium_primaryBtn" onClick={() => buscarMaquinas({ silent: false })}>
                Atualizar dados
              </button>
            </div>

            <div className="DashboardPremium_list">
              <div className="DashboardPremium_listItem">
                <div className="DashboardPremium_listTitle">Monitore sinal baixo</div>
                <div className="DashboardPremium_listRight">Evita quedas</div>
              </div>
              <div className="DashboardPremium_listItem">
                <div className="DashboardPremium_listTitle">Revise máquinas offline</div>
                <div className="DashboardPremium_listRight">Recupera vendas</div>
              </div>
              <div className="DashboardPremium_listItem">
                <div className="DashboardPremium_listTitle">Veja pagamentos por máquina</div>
                <div className="DashboardPremium_listRight">Detalhar</div>
              </div>
            </div>
          </div>

          <div className="DashboardPremium_card">
            <div className="DashboardPremium_cardHeader">
              <div className="DashboardPremium_cardTitle">Dicas para suas máquinas</div>
            </div>
            <div className="DashboardPremium_tip">
              <div className="DashboardPremium_tipIcon">🎁</div>
              <div>
                <div className="DashboardPremium_tipTitle">Prêmios em destaque</div>
                <div className="DashboardPremium_tipDesc">
                  Coloque os prêmios maiores na frente e mantenha o estoque organizado.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="DashboardPremium_bottom">
        <div className="DashboardPremium_card DashboardPremium_bottomCard">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Resumo (Hoje + Ontem)</div>
          </div>
          <div className="DashboardPremium_bigValue">R$ {fmtBRL(resumo.faturamentoHojeTotal + resumo.faturamentoOntemTotal)}</div>
          <div className="DashboardPremium_smallHint">Somatório de faturamento informado no monitoramento</div>
        </div>
      </div>
    </div>
  );
};

export const MinhasMaquinasPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");

  const buscarMaquinas = ({ silent } = { silent: false }) => {
    if (!silent) setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas({ silent: false });
    const interval = setInterval(() => {
      buscarMaquinas({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const list = (() => {
    const raw = Array.isArray(maquinas) ? maquinas : [];
    const q = String(query || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((m) => {
      const nome = String(m?.nome || "").toLowerCase();
      const local = String(m?.store_id || "").toLowerCase();
      return nome.includes(q) || local.includes(q);
    });
  })();

  const resumo = (() => {
    const faturamentoHojeTotal = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoHoje), 0);
    const offlineCount = list.filter((m) => m?.status === "OFFLINE").length;
    const totalMaquinas = list.length;
    const ticketMedio = totalMaquinas ? faturamentoHojeTotal / totalMaquinas : 0;
    return { faturamentoHojeTotal, offlineCount, totalMaquinas, ticketMedio };
  })();

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="DashboardPremiumMachines_container">
      <div className="DashboardPremiumMachines_header">
        <div>
          <div className="DashboardPremiumMachines_title">Minhas Máquinas</div>
          <div className="DashboardPremiumMachines_sub">Versão premium</div>
        </div>
        <div className="DashboardPremiumMachines_actions">
          <button
            type="button"
            className="DashboardPremiumMachines_iconBtn"
            onClick={() => showMaintenance("Pesquisar")}
          >
            <i className="bi bi-search"></i>
          </button>
          <button
            type="button"
            className="DashboardPremiumMachines_iconBtn DashboardPremiumMachines_iconBtnPrimary"
            onClick={() => showMaintenance("Adicionar")}
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>

      <div className="DashboardPremiumMachines_search">
        <input
          className="DashboardPremiumMachines_input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por máquina ou local..."
        />
        <button
          type="button"
          className="DashboardPremiumMachines_filterBtn"
          onClick={() => showMaintenance("Filtros")}
        >
          <i className="bi bi-funnel"></i> Filtros
        </button>
      </div>

      <div className="DashboardPremium_stats DashboardPremiumMachines_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento hoje</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">📈</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoHojeTotal)}</div>
          <div className="DashboardPremium_statHint">{resumo.totalMaquinas} máquina(s)</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Ticket médio</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">💳</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.ticketMedio)}</div>
          <div className="DashboardPremium_statHint">Estimativa por máquina</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Oportunidades</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">💡</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.offlineCount}</div>
          <div className="DashboardPremium_statHint">Máquinas offline</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Jogadas hoje</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">🎮</div>
          </div>
          <div className="DashboardPremium_statValue">-</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>
      </div>

      <div className="DashboardPremiumMachines_section">
        <div className="DashboardPremiumMachines_sectionTitle">Todas as Máquinas</div>
        <div className="DashboardPremiumMachines_list">
          {list.map((m) => {
            const isOnline = m?.status === "ONLINE" || m?.status === "PAGAMENTO_RECENTE";
            const pagamentoRecente = m?.status === "PAGAMENTO_RECENTE";
            const statusLabel = m?.status === "OFFLINE" ? "Offline" : pagamentoRecente ? "Pagamento recente" : "Online";
            const statusClass =
              m?.status === "OFFLINE"
                ? "DashboardPremium_badgeRed"
                : pagamentoRecente
                  ? "DashboardPremium_badgeBlue"
                  : "DashboardPremium_badgeGreen";
            const receptionIcon = getReceptionIcon(m?.nivelDeSinal);
            return (
              <div key={m.id} className="DashboardPremiumMachines_card">
                <div className="DashboardPremiumMachines_cardTop">
                  <div className="DashboardPremiumMachines_cardLeft">
                    <div className="DashboardPremiumMachines_machineName">{m?.nome || "-"}</div>
                    <div className="DashboardPremiumMachines_machineSub">
                      <span className={isOnline ? "DashboardPremium_dotOn" : "DashboardPremium_dotOff"}></span>
                      <span>{m?.store_id || "-"}</span>
                      <span className="DashboardPremium_signal">
                        <i className={`bi bi-reception-${receptionIcon}`}></i> {m?.nivelDeSinal ? `${m.nivelDeSinal}%` : "0%"}
                      </span>
                    </div>
                    <div className="DashboardPremiumMachines_status">
                      <span className={`DashboardPremium_badge ${statusClass}`}>{statusLabel}</span>
                    </div>
                  </div>
                  <div className="DashboardPremiumMachines_cardRight">
                    <div className="DashboardPremiumMachines_money">R$ {fmtBRL(toNumberMoney(m?.faturamentoHoje))}</div>
                    <div className="DashboardPremiumMachines_moneyHint">Hoje</div>
                  </div>
                </div>

                <div className="DashboardPremiumMachines_cardBottom">
                  <button
                    type="button"
                    className="DashboardPremiumMachines_boostBtn"
                    onClick={() => showMaintenance("Turbinar")}
                  >
                    Turbinar
                  </button>
                </div>
              </div>
            );
          })}
          {!list.length && (
            <div className="DashboardPremiumMachines_empty">Nenhuma máquina encontrada.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const RelatoriosPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [range, setRange] = useState(() => {
    try {
      const now = new Date();
      const end = now.toISOString().slice(0, 10);
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      const start = startDate.toISOString().slice(0, 10);
      return { start, end };
    } catch (e) {
      return { start: "", end: "" };
    }
  });

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const faturamentoTotal = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoHoje), 0);
    const totalJogadas = 0;
    const premiosEntregues = 0;
    const ticketMedio = list.length ? faturamentoTotal / list.length : 0;
    return { faturamentoTotal, totalJogadas, premiosEntregues, ticketMedio };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumReports_container">
      <div className="PremiumReports_header">
        <div>
          <div className="PremiumReports_title">Relatórios</div>
          <div className="PremiumReports_sub">Acompanhe o desempenho das suas máquinas</div>
        </div>

        <div className="PremiumReports_range">
          <input
            className="PremiumReports_date"
            type="date"
            value={range.start}
            onChange={(e) => setRange((prev) => ({ ...prev, start: e.target.value }))}
          />
          <span className="PremiumReports_rangeSep">-</span>
          <input
            className="PremiumReports_date"
            type="date"
            value={range.end}
            onChange={(e) => setRange((prev) => ({ ...prev, end: e.target.value }))}
          />
          <button
            type="button"
            className="PremiumReports_applyBtn"
            onClick={() => showMaintenance("Aplicar período")}
          >
            Aplicar
          </button>
        </div>
      </div>

      <div className="DashboardPremium_stats PremiumReports_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento total</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">💰</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoTotal)}</div>
          <div className="DashboardPremium_statHint">Base: faturamento de hoje</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Total de jogadas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">🎮</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.totalJogadas}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Prêmios entregues</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">🎁</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.premiosEntregues}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Ticket médio</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">🧾</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.ticketMedio)}</div>
          <div className="DashboardPremium_statHint">Estimativa por máquina</div>
        </div>
      </div>

      <div className="PremiumReports_grid">
        <div className="DashboardPremium_card PremiumReports_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Faturamento por dia</div>
            <div className="PremiumReports_pills">
              <button type="button" className="PremiumReports_pill" onClick={() => showMaintenance("7 dias")}>7 dias</button>
              <button type="button" className="PremiumReports_pill PremiumReports_pillActive" onClick={() => showMaintenance("30 dias")}>30 dias</button>
              <button type="button" className="PremiumReports_pill" onClick={() => showMaintenance("90 dias")}>90 dias</button>
              <button type="button" className="PremiumReports_pill" onClick={() => showMaintenance("Personalizado")}>Personalizado</button>
            </div>
          </div>
          <div className="PremiumReports_placeholder">
            Gráfico em desenvolvimento
          </div>
        </div>

        <div className="DashboardPremium_card PremiumReports_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Faturamento por forma de pagamento</div>
          </div>
          <div className="PremiumReports_placeholder">
            Gráfico em desenvolvimento
          </div>
        </div>

        <div className="DashboardPremium_card PremiumReports_card PremiumReports_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Desempenho por máquina</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver relatório completo")}>
              Ver relatório completo
            </button>
          </div>
          <div className="PremiumReports_table">
            <div className="PremiumReports_tableHead">
              <div>Máquina</div>
              <div>Faturamento</div>
              <div>Status</div>
            </div>
            {maquinas.slice(0, 5).map((m) => (
              <div key={m.id} className="PremiumReports_tableRow">
                <div className="PremiumReports_tableMachine">{m?.nome || "-"}</div>
                <div className="PremiumReports_tableMoney">R$ {fmtBRL(toNumberMoney(m?.faturamentoHoje))}</div>
                <div className="PremiumReports_tableStatus">{m?.status || "-"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="PremiumReports_actions">
        <button type="button" className="PremiumReports_actionBtn" onClick={() => showMaintenance("Exportar relatório")}>
          <i className="bi bi-download"></i> Exportar relatório
        </button>
        <button type="button" className="PremiumReports_actionBtn PremiumReports_actionPrimary" onClick={() => showMaintenance("Aplicar melhorias sugeridas")}>
          <i className="bi bi-lightning-charge"></i> Aplicar melhorias sugeridas
        </button>
      </div>
    </div>
  );
};

export const FinanceiroPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [range, setRange] = useState(() => {
    try {
      const now = new Date();
      const end = now.toISOString().slice(0, 10);
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      const start = startDate.toISOString().slice(0, 10);
      return { start, end };
    } catch (e) {
      return { start: "", end: "" };
    }
  });

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const faturamentoBruto = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoHoje), 0);
    const custosETaxas = 0;
    const lucroLiquido = faturamentoBruto - custosETaxas;
    const margemLucro = faturamentoBruto > 0 ? (lucroLiquido / faturamentoBruto) * 100 : 0;
    return { faturamentoBruto, custosETaxas, lucroLiquido, margemLucro };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumFinance_container">
      <div className="PremiumFinance_header">
        <div>
          <div className="PremiumFinance_title">Financeiro</div>
          <div className="PremiumFinance_sub">Acompanhe entradas e saídas, saiba se há lucro do seu negócio.</div>
        </div>

        <div className="PremiumFinance_range">
          <input
            className="PremiumFinance_date"
            type="date"
            value={range.start}
            onChange={(e) => setRange((prev) => ({ ...prev, start: e.target.value }))}
          />
          <span className="PremiumFinance_rangeSep">-</span>
          <input
            className="PremiumFinance_date"
            type="date"
            value={range.end}
            onChange={(e) => setRange((prev) => ({ ...prev, end: e.target.value }))}
          />
          <button
            type="button"
            className="PremiumFinance_applyBtn"
            onClick={() => showMaintenance("Aplicar período")}
          >
            Aplicar
          </button>
        </div>
      </div>

      <div className="DashboardPremium_stats PremiumFinance_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento Bruto</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">💰</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoBruto)}</div>
          <div className="DashboardPremium_statHint">Base: faturamento de hoje</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Custos e Taxas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">🧾</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.custosETaxas)}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Lucro Líquido</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">📈</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.lucroLiquido)}</div>
          <div className="DashboardPremium_statHint">Estimativa</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Margem de Lucro</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">%</div>
          </div>
          <div className="DashboardPremium_statValue">{fmtBRL(resumo.margemLucro)}%</div>
          <div className="DashboardPremium_statHint">Estimativa</div>
        </div>
      </div>

      <div className="PremiumFinance_grid">
        <div className="DashboardPremium_card PremiumFinance_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Faturamento por dia</div>
            <div className="PremiumFinance_pills">
              <button type="button" className="PremiumFinance_pill" onClick={() => showMaintenance("7 dias")}>7 dias</button>
              <button type="button" className="PremiumFinance_pill PremiumFinance_pillActive" onClick={() => showMaintenance("30 dias")}>30 dias</button>
              <button type="button" className="PremiumFinance_pill" onClick={() => showMaintenance("Trimestral")}>Trimestral</button>
              <button type="button" className="PremiumFinance_pill" onClick={() => showMaintenance("Personalizado")}>Personalizado</button>
            </div>
          </div>
          <div className="PremiumFinance_placeholder">Gráfico em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumFinance_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Faturamento por forma de pagamento</div>
          </div>
          <div className="PremiumFinance_placeholder">Gráfico em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumFinance_card PremiumFinance_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Últimas Entradas</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumFinance_table">
            <div className="PremiumFinance_tableHead">
              <div>Máquina</div>
              <div>Valor</div>
              <div>Data</div>
            </div>
            {maquinas.slice(0, 5).map((m) => (
              <div key={m.id} className="PremiumFinance_tableRow">
                <div className="PremiumFinance_tableMachine">{m?.nome || "-"}</div>
                <div className="PremiumFinance_tableMoney">R$ {fmtBRL(toNumberMoney(m?.faturamentoHoje))}</div>
                <div className="PremiumFinance_tableMeta">Hoje</div>
              </div>
            ))}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumFinance_card PremiumFinance_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Últimas Saídas</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumFinance_placeholder PremiumFinance_placeholderSmall">Em desenvolvimento</div>
        </div>
      </div>

      <div className="PremiumFinance_actions">
        <button type="button" className="PremiumFinance_actionBtn" onClick={() => showMaintenance("Exportar relatório")}>
          <i className="bi bi-download"></i> Exportar Relatório
        </button>
        <button type="button" className="PremiumFinance_actionBtn PremiumFinance_actionPrimary" onClick={() => showMaintenance("Comparar períodos")}>
          <i className="bi bi-bar-chart"></i> Comparar Períodos
        </button>
      </div>
    </div>
  );
};

export const PromocoesPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const promocoesAtivas = 0;
    const jogadasGeradas = 0;
    const bonusConcedidos = 0;
    const faturamentoGerado = list.reduce((acc, m) => acc + toNumberMoney(m?.faturamentoHoje), 0);
    return { promocoesAtivas, jogadasGeradas, bonusConcedidos, faturamentoGerado };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumPromos_container">
      <div className="PremiumPromos_header">
        <div>
          <div className="PremiumPromos_title">Promoções</div>
          <div className="PremiumPromos_sub">
            Crie e gerencie promoções para aumentar as jogadas e o faturamento das suas máquinas.
          </div>
        </div>
        <button
          type="button"
          className="PremiumPromos_newBtn"
          onClick={() => showMaintenance("Nova Promoção")}
        >
          <i className="bi bi-plus-lg"></i> Nova Promoção
        </button>
      </div>

      <div className="DashboardPremium_stats PremiumPromos_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Promoções ativas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">🎁</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.promocoesAtivas}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Jogadas geradas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">🎮</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.jogadasGeradas}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Bônus concedidos</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">🏷</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.bonusConcedidos}</div>
          <div className="DashboardPremium_statHint">Em breve</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento gerado</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">💰</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoGerado)}</div>
          <div className="DashboardPremium_statHint">Base: faturamento de hoje</div>
        </div>
      </div>

      <div className="PremiumPromos_grid">
        <div className="DashboardPremium_card PremiumPromos_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Desempenho das Promoções</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Período")}>
              Últimos 30 dias
            </button>
          </div>
          <div className="PremiumPromos_placeholder">Gráfico em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumPromos_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Tipos de Promoção</div>
          </div>
          <div className="PremiumPromos_placeholder">Gráfico em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumPromos_card PremiumPromos_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Promoções Ativas</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumPromos_table">
            <div className="PremiumPromos_tableHead">
              <div>Promoção</div>
              <div>Máquina</div>
              <div>Status</div>
            </div>
            {maquinas.slice(0, 5).map((m) => (
              <div key={m.id} className="PremiumPromos_tableRow">
                <div className="PremiumPromos_tablePromo">Em desenvolvimento</div>
                <div className="PremiumPromos_tableMachine">{m?.nome || "-"}</div>
                <div className="PremiumPromos_tableStatus">-</div>
              </div>
            ))}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumPromos_card PremiumPromos_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Ações rápidas</div>
          </div>
          <div className="PremiumPromos_quickGrid">
            <button type="button" className="PremiumPromos_quickBtn" onClick={() => showMaintenance("Criar QR Code premiado")}>
              <i className="bi bi-qr-code"></i> Criar QR Code Premiando
            </button>
            <button type="button" className="PremiumPromos_quickBtn" onClick={() => showMaintenance("Criar link de bônus")}>
              <i className="bi bi-link-45deg"></i> Criar Link de Bônus
            </button>
            <button type="button" className="PremiumPromos_quickBtn" onClick={() => showMaintenance("Ver relatório")}>
              <i className="bi bi-bar-chart"></i> Ver Relatório de Promoções
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AlertasPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ tipo: "todos", maquina: "todas" });

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const criticos = list.filter((m) => m?.status === "OFFLINE").length;
    const atencao = list.filter((m) => Number(m?.nivelDeSinal || 0) > 0 && Number(m?.nivelDeSinal || 0) <= 30).length;
    const informativos = list.filter((m) => m?.status === "PAGAMENTO_RECENTE").length;
    const resolvidos = 0;
    return { criticos, atencao, informativos, resolvidos };
  })();

  const alertsList = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const base = [];
    for (const m of list) {
      if (m?.status === "OFFLINE") {
        base.push({
          key: `${m.id}-offline`,
          tipo: "critico",
          titulo: "Máquina offline detectada",
          desc: `${m?.nome || "Máquina"} está offline. Verifique energia, internet e sinal.`,
          maquina: m?.nome || "-",
          local: m?.store_id || "-",
          badge: "Crítico",
        });
      } else if (Number(m?.nivelDeSinal || 0) > 0 && Number(m?.nivelDeSinal || 0) <= 30) {
        base.push({
          key: `${m.id}-signal`,
          tipo: "atencao",
          titulo: "Sinal baixo",
          desc: `${m?.nome || "Máquina"} está com sinal baixo. Considere reposicionar ou revisar conexão.`,
          maquina: m?.nome || "-",
          local: m?.store_id || "-",
          badge: "Atenção",
        });
      } else if (m?.status === "PAGAMENTO_RECENTE") {
        base.push({
          key: `${m.id}-recent`,
          tipo: "info",
          titulo: "Pagamento recente",
          desc: `${m?.nome || "Máquina"} recebeu pagamento recentemente.`,
          maquina: m?.nome || "-",
          local: m?.store_id || "-",
          badge: "Informativo",
        });
      }
    }

    const tipo = String(filters.tipo || "todos");
    const maquina = String(filters.maquina || "todas");
    return base
      .filter((a) => (tipo === "todos" ? true : a.tipo === tipo))
      .filter((a) => (maquina === "todas" ? true : a.maquina === maquina));
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumAlerts_container">
      <div className="PremiumAlerts_header">
        <div>
          <div className="PremiumAlerts_title">Alertas</div>
          <div className="PremiumAlerts_sub">
            Acompanhe os avisos importantes e tome ações para aumentar seus resultados.
          </div>
        </div>
        <div className="PremiumAlerts_headerActions">
          <button
            type="button"
            className="PremiumAlerts_iconBtn"
            onClick={() => showMaintenance("Histórico de Alertas")}
          >
            <i className="bi bi-clock-history"></i> Histórico de Alertas
          </button>
        </div>
      </div>

      <div className="DashboardPremium_stats PremiumAlerts_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Críticos</div>
            <div className="DashboardPremium_statIcon DashboardPremium_badgeRed">!</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.criticos}</div>
          <div className="DashboardPremium_statHint">Exigem atenção imediata</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Atenção</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">⚠</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.atencao}</div>
          <div className="DashboardPremium_statHint">Precisam de melhorias</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Informativos</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">i</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.informativos}</div>
          <div className="DashboardPremium_statHint">Acompanhe e fique atento</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Resolvidos</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">✓</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.resolvidos}</div>
          <div className="DashboardPremium_statHint">Últimos 30 dias</div>
        </div>
      </div>

      <div className="PremiumAlerts_grid">
        <div className="DashboardPremium_card PremiumAlerts_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Todos os Alertas</div>
            <div className="PremiumAlerts_filters">
              <select
                className="PremiumAlerts_select"
                value={filters.tipo}
                onChange={(e) => setFilters((prev) => ({ ...prev, tipo: e.target.value }))}
              >
                <option value="todos">Todos os tipos</option>
                <option value="critico">Crítico</option>
                <option value="atencao">Atenção</option>
                <option value="info">Informativo</option>
              </select>
              <select
                className="PremiumAlerts_select"
                value={filters.maquina}
                onChange={(e) => setFilters((prev) => ({ ...prev, maquina: e.target.value }))}
              >
                <option value="todas">Todas as máquinas</option>
                {[...new Set(maquinas.map((m) => m?.nome).filter(Boolean))].slice(0, 15).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <button type="button" className="PremiumAlerts_filterBtn" onClick={() => showMaintenance("Filtros")}>
                <i className="bi bi-funnel"></i>
              </button>
            </div>
          </div>

          <div className="PremiumAlerts_list">
            {alertsList.slice(0, 10).map((a) => (
              <div key={a.key} className="PremiumAlerts_item">
                <div className="PremiumAlerts_itemLeft">
                  <div className="PremiumAlerts_itemTitle">{a.titulo}</div>
                  <div className="PremiumAlerts_itemDesc">{a.desc}</div>
                </div>
                <div className="PremiumAlerts_itemRight">
                  <div className="PremiumAlerts_itemMeta">{a.local}</div>
                  <div className="PremiumAlerts_itemMeta">{a.maquina}</div>
                  <span className={`DashboardPremium_badge ${a.tipo === "critico" ? "DashboardPremium_badgeRed" : a.tipo === "atencao" ? "DashboardPremium_badgeBlue" : "DashboardPremium_badgeGreen"}`}>
                    {a.badge}
                  </span>
                </div>
              </div>
            ))}
            {!alertsList.length && (
              <div className="PremiumAlerts_empty">Nenhum alerta encontrado.</div>
            )}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumAlerts_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Máquinas em risco</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumAlerts_placeholder">Em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumAlerts_card PremiumAlerts_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Ações recomendadas</div>
          </div>
          <div className="PremiumAlerts_actions">
            <button type="button" className="PremiumAlerts_actionBtn" onClick={() => showMaintenance("Ativar promoção")}>
              <i className="bi bi-megaphone"></i> Ativar promoção na máquina
            </button>
            <button type="button" className="PremiumAlerts_actionBtn" onClick={() => showMaintenance("Reduzir taxa")}>
              <i className="bi bi-percent"></i> Reduzir taxa de prêmio
            </button>
            <button type="button" className="PremiumAlerts_actionBtn" onClick={() => showMaintenance("Reabastecer")}>
              <i className="bi bi-box-seam"></i> Reabastecer pelúcias
            </button>
            <button type="button" className="PremiumAlerts_actionBtn PremiumAlerts_actionPrimary" onClick={() => showMaintenance("Ver todas as ações")}>
              Ver todas as ações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EstrategiasPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const estrategiasRecomendadas = [
    {
      key: "qr-premiado",
      title: "Ativar QR Code Premiando",
      impactLabel: "Alto impacto",
      impact: "+R$ 742,50/mês",
      desc: "Máquinas com QR Code ativo têm +28% de jogadas em média.",
    },
    {
      key: "pelucias",
      title: "Trocar pelúcias com baixa saída",
      impactLabel: "Médio impacto",
      impact: "+R$ 418,20/mês",
      desc: "Pelúcias populares aumentam a chance de ganharem.",
    },
    {
      key: "happy-hour",
      title: "Criar promoção de horário feliz",
      impactLabel: "Alto impacto",
      impact: "+R$ 586,00/mês",
      desc: "Promoções em horários de menor movimento aumentam o fluxo.",
    },
    {
      key: "redes",
      title: "Divulgar nas redes sociais",
      impactLabel: "Médio impacto",
      impact: "+R$ 276,30/mês",
      desc: "Máquinas divulgadas semanalmente têm +18% em engajamento.",
    },
  ];

  const resumo = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const estrategiasAtivas = 0;
    const maquinasImpactadas = Math.min(list.length, 8);
    const taxaSucesso = 76;
    const impactoEstimado = 2457.8;
    return { estrategiasAtivas, maquinasImpactadas, taxaSucesso, impactoEstimado };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumStrategies_container">
      <div className="PremiumStrategies_header">
        <div>
          <div className="PremiumStrategies_title">Estratégias</div>
          <div className="PremiumStrategies_sub">
            Aplique as melhores práticas para aumentar o desempenho das suas máquinas.
          </div>
        </div>
        <button type="button" className="PremiumStrategies_newBtn" onClick={() => showMaintenance("Nova Estratégia")}>
          <i className="bi bi-plus-lg"></i> Nova Estratégia
        </button>
      </div>

      <div className="DashboardPremium_stats PremiumStrategies_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Estratégias ativas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">🧠</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.estrategiasAtivas}</div>
          <div className="DashboardPremium_statHint">Em execução</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Impacto estimado (mês)</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">⚖</div>
          </div>
          <div className="DashboardPremium_statValue">+R$ {fmtBRL(resumo.impactoEstimado)}</div>
          <div className="DashboardPremium_statHint">Aumento potencial</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Máquinas impactadas</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">📍</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.maquinasImpactadas}</div>
          <div className="DashboardPremium_statHint">Do total</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Taxa de sucesso</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">%</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.taxaSucesso}%</div>
          <div className="DashboardPremium_statHint">Média das estratégias</div>
        </div>
      </div>

      <div className="PremiumStrategies_banner">
        <div className="PremiumStrategies_bannerText">
          <div className="PremiumStrategies_bannerTitle">Aplique estratégias e veja seus resultados decolarem!</div>
          <div className="PremiumStrategies_bannerSub">
            Máquinas que aplicam 3 ou mais estratégias faturam até 32% mais.
          </div>
        </div>
        <button type="button" className="PremiumStrategies_bannerBtn" onClick={() => showMaintenance("Como funciona")}>
          <i className="bi bi-play-circle"></i> Ver como funciona
        </button>
      </div>

      <div className="PremiumStrategies_grid">
        <div className="DashboardPremium_card PremiumStrategies_card PremiumStrategies_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Estratégias recomendadas para você</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumStrategies_recoGrid">
            {estrategiasRecomendadas.map((s) => (
              <div key={s.key} className="PremiumStrategies_recoCard">
                <div className="PremiumStrategies_recoTop">
                  <span className="PremiumStrategies_recoChip">{s.impactLabel}</span>
                </div>
                <div className="PremiumStrategies_recoTitle">{s.title}</div>
                <div className="PremiumStrategies_recoDesc">{s.desc}</div>
                <div className="PremiumStrategies_recoImpact">Impacto estimado: <strong>{s.impact}</strong></div>
                <button type="button" className="PremiumStrategies_applyBtn" onClick={() => showMaintenance("Aplicar estratégia")}>
                  Aplicar estratégia
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumStrategies_card PremiumStrategies_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Estratégias ativas</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver relatório")}>
              Ver relatório
            </button>
          </div>
          <div className="PremiumStrategies_placeholder">Tabela em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumStrategies_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Desempenho das estratégias</div>
          </div>
          <div className="PremiumStrategies_placeholder PremiumStrategies_placeholderSmall">Em desenvolvimento</div>
          <button type="button" className="PremiumStrategies_secondaryBtn" onClick={() => showMaintenance("Ver relatório completo")}>
            Ver relatório completo
          </button>
        </div>

        <div className="DashboardPremium_card PremiumStrategies_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Categorias de estratégias</div>
          </div>
          <div className="PremiumStrategies_placeholder PremiumStrategies_placeholderSmall">Em desenvolvimento</div>
          <button type="button" className="PremiumStrategies_secondaryBtn" onClick={() => showMaintenance("Ver todas")}>
            Ver todas as estratégias
          </button>
        </div>
      </div>
    </div>
  );
};

export const PeluciasPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const pelucias = (() => {
    const base = [
      { key: "plu-001", nome: "Ursinho Caramelo", categoria: "Ursos", estoque: 210, custo: 8.5, saidas30: 186, giro: 4.1, status: "Em Alta" },
      { key: "plu-002", nome: "Panda Fofo", categoria: "Ursos", estoque: 150, custo: 9.2, saidas30: 128, giro: 3.6, status: "Em Alta" },
      { key: "plu-003", nome: "Coelho Rosa", categoria: "Coelhos", estoque: 98, custo: 7.8, saidas30: 56, giro: 2.1, status: "Média" },
      { key: "plu-004", nome: "Stitch Azul", categoria: "Personagens", estoque: 76, custo: 10.9, saidas30: 82, giro: 3.2, status: "Em Alta" },
      { key: "plu-005", nome: "Pikachu", categoria: "Personagens", estoque: 40, custo: 11.5, saidas30: 28, giro: 1.4, status: "Em Baixa" },
      { key: "plu-006", nome: "Dinossauro Verde", categoria: "Dinossauros", estoque: 32, custo: 8.2, saidas30: 18, giro: 1.2, status: "Em Baixa" },
      { key: "plu-007", nome: "Leãozinho", categoria: "Animais", estoque: 0, custo: 8.9, saidas30: 24, giro: 0, status: "Sem Estoque" },
      { key: "plu-008", nome: "Unicórnio Branco", categoria: "Unicórnios", estoque: 18, custo: 9.9, saidas30: 9, giro: 0.9, status: "Em Baixa" },
    ];
    const q = String(query || "").trim().toLowerCase();
    if (!q) return base;
    return base.filter((p) => String(p.nome).toLowerCase().includes(q) || String(p.categoria).toLowerCase().includes(q));
  })();

  const resumo = (() => {
    const total = pelucias.reduce((acc, p) => acc + (Number(p.estoque) || 0), 0);
    const valor = pelucias.reduce((acc, p) => acc + (Number(p.estoque) || 0) * (Number(p.custo) || 0), 0);
    const emAlta = pelucias.filter((p) => p.status === "Em Alta").length;
    const emBaixa = pelucias.filter((p) => p.status === "Em Baixa").length;
    const semEstoque = pelucias.filter((p) => p.status === "Sem Estoque").length;
    const giroMedio = pelucias.length ? pelucias.reduce((acc, p) => acc + (Number(p.giro) || 0), 0) / pelucias.length : 0;
    return { total, valor, emAlta, emBaixa, semEstoque, giroMedio };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumPlush_container">
      <div className="PremiumPlush_header">
        <div>
          <div className="PremiumPlush_title">Pelúcias</div>
          <div className="PremiumPlush_sub">
            Gerencie o estoque e o desempenho das pelúcias nas suas máquinas.
          </div>
        </div>
        <div className="PremiumPlush_headerRight">
          <input
            className="PremiumPlush_search"
            placeholder="Buscar pelúcia..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="PremiumPlush_btn" onClick={() => showMaintenance("Filtros")}>
            <i className="bi bi-funnel"></i> Filtros
          </button>
          <button type="button" className="PremiumPlush_btn PremiumPlush_btnPrimary" onClick={() => showMaintenance("Adicionar Pelúcia")}>
            <i className="bi bi-plus-lg"></i> Adicionar Pelúcia
          </button>
        </div>
      </div>

      <div className="DashboardPremium_stats PremiumPlush_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Total de pelúcias</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">🧸</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.total}</div>
          <div className="DashboardPremium_statHint">em estoque total</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Valor em estoque</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">💰</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.valor)}</div>
          <div className="DashboardPremium_statHint">custo total das pelúcias</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Pelúcias em alta</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">⬆</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.emAlta}</div>
          <div className="DashboardPremium_statHint">alta saída nas máquinas</div>
        </div>

        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Em baixa</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">⬇</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.emBaixa}</div>
          <div className="DashboardPremium_statHint">baixa saída</div>
        </div>
      </div>

      <div className="PremiumPlush_grid">
        <div className="DashboardPremium_card PremiumPlush_card PremiumPlush_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Todas as Pelúcias</div>
          </div>
          <div className="PremiumPlush_table">
            <div className="PremiumPlush_tableHead">
              <div>Pelúcia</div>
              <div>Categoria</div>
              <div>Estoque</div>
              <div>Custo Unit.</div>
              <div>Saídas (30d)</div>
              <div>Giro (30d)</div>
              <div>Status</div>
              <div>Ações</div>
            </div>
            {pelucias.map((p) => (
              <div key={p.key} className="PremiumPlush_tableRow">
                <div className="PremiumPlush_name">{p.nome}</div>
                <div className="PremiumPlush_cat">{p.categoria}</div>
                <div>{p.estoque}</div>
                <div>R$ {fmtBRL(p.custo)}</div>
                <div>{p.saidas30}</div>
                <div>{String(p.giro).replace(".", ",")}x</div>
                <div>
                  <span className={`PremiumPlush_badge ${p.status === "Em Alta" ? "PremiumPlush_badgeUp" : p.status === "Em Baixa" ? "PremiumPlush_badgeDown" : p.status === "Sem Estoque" ? "PremiumPlush_badgeOut" : "PremiumPlush_badgeMid"}`}>
                    {p.status}
                  </span>
                </div>
                <div>
                  <button type="button" className="PremiumPlush_iconBtn" onClick={() => showMaintenance("Editar")}>
                    <i className="bi bi-pencil-square"></i>
                  </button>
                </div>
              </div>
            ))}
            {!pelucias.length && <div className="PremiumPlush_empty">Nenhuma pelúcia encontrada.</div>}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumPlush_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Alertas de estoque</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todas")}>
              Ver todas
            </button>
          </div>
          <div className="PremiumPlush_placeholder">Em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumPlush_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Estoque por categoria</div>
          </div>
          <div className="PremiumPlush_placeholder">Em desenvolvimento</div>
        </div>
      </div>
    </div>
  );
};

export const LocalizacoesPremium = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [range, setRange] = useState(() => {
    try {
      const now = new Date();
      const end = now.toISOString().slice(0, 10);
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      const start = startDate.toISOString().slice(0, 10);
      return { start, end };
    } catch (e) {
      return { start: "", end: "" };
    }
  });

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const buscarMaquinas = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas?clienteId=${authInfo?.dataUser.clienteId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        setMaquinas(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({
            type: "error",
            message: "Sessão expirada",
          });
          setDataUser(null);
          navigate(links.SIGNIN);
        }
      });
  };

  useEffect(() => {
    buscarMaquinas();
  }, []);

  const locais = (() => {
    const list = Array.isArray(maquinas) ? maquinas : [];
    const map = new Map();
    for (const m of list) {
      const key = String(m?.store_id || "Sem local");
      if (!map.has(key)) {
        map.set(key, { local: key, maquinas: 0, faturamento: 0, offline: 0 });
      }
      const row = map.get(key);
      row.maquinas += 1;
      row.faturamento += toNumberMoney(m?.faturamentoHoje);
      if (m?.status === "OFFLINE") row.offline += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.faturamento - a.faturamento);
  })();

  const resumo = (() => {
    const totalLocais = locais.length;
    const faturamentoTotal = locais.reduce((acc, l) => acc + l.faturamento, 0);
    const lucroLiquido = faturamentoTotal * 0.66;
    const conversaoMedia = 18.6;
    const destaque = locais[0]?.local || "-";
    return { totalLocais, faturamentoTotal, lucroLiquido, conversaoMedia, destaque };
  })();

  if (isLoading) {
    return <LoadingAction />;
  }

  return (
    <div className="PremiumLocations_container">
      <div className="PremiumLocations_header">
        <div>
          <div className="PremiumLocations_title">Localizações</div>
          <div className="PremiumLocations_sub">
            Acompanhe o desempenho de cada ponto de instalação das suas máquinas.
          </div>
        </div>

        <div className="PremiumLocations_headerRight">
          <input
            className="PremiumLocations_date"
            type="date"
            value={range.start}
            onChange={(e) => setRange((prev) => ({ ...prev, start: e.target.value }))}
          />
          <input
            className="PremiumLocations_date"
            type="date"
            value={range.end}
            onChange={(e) => setRange((prev) => ({ ...prev, end: e.target.value }))}
          />
          <button type="button" className="PremiumLocations_addBtn" onClick={() => showMaintenance("Adicionar Localização")}>
            <i className="bi bi-plus-lg"></i> Adicionar Localização
          </button>
        </div>
      </div>

      <div className="DashboardPremium_stats PremiumLocations_stats">
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Total de localizações</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconPurple">📍</div>
          </div>
          <div className="DashboardPremium_statValue">{resumo.totalLocais}</div>
          <div className="DashboardPremium_statHint">ativo(s) no período</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Faturamento total</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconGreen">💰</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.faturamentoTotal)}</div>
          <div className="DashboardPremium_statHint">Base: faturamento de hoje</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Lucro líquido</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconYellow">📈</div>
          </div>
          <div className="DashboardPremium_statValue">R$ {fmtBRL(resumo.lucroLiquido)}</div>
          <div className="DashboardPremium_statHint">Estimativa</div>
        </div>
        <div className="DashboardPremium_statCard">
          <div className="DashboardPremium_statTop">
            <div className="DashboardPremium_statLabel">Conversão média</div>
            <div className="DashboardPremium_statIcon DashboardPremium_iconOrange">%</div>
          </div>
          <div className="DashboardPremium_statValue">{fmtBRL(resumo.conversaoMedia)}%</div>
          <div className="DashboardPremium_statHint">Estimativa</div>
        </div>
      </div>

      <div className="PremiumLocations_grid">
        <div className="DashboardPremium_card PremiumLocations_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Desempenho por localização</div>
            <div className="PremiumLocations_actions">
              <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Exportar")}>
                Exportar
              </button>
            </div>
          </div>
          <div className="PremiumLocations_table">
            <div className="PremiumLocations_tableHead">
              <div>Localização</div>
              <div>Máquinas</div>
              <div>Faturamento</div>
              <div>Offline</div>
            </div>
            {locais.slice(0, 8).map((l) => (
              <div key={l.local} className="PremiumLocations_tableRow">
                <div className="PremiumLocations_tableLoc">{l.local}</div>
                <div>{l.maquinas}</div>
                <div className="PremiumLocations_money">R$ {fmtBRL(l.faturamento)}</div>
                <div>{l.offline}</div>
              </div>
            ))}
            {!locais.length && <div className="PremiumLocations_empty">Nenhuma localização encontrada.</div>}
          </div>
        </div>

        <div className="DashboardPremium_card PremiumLocations_card">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Mapa das Localizações</div>
          </div>
          <div className="PremiumLocations_placeholder">Mapa em desenvolvimento</div>
        </div>

        <div className="DashboardPremium_card PremiumLocations_card PremiumLocations_full">
          <div className="DashboardPremium_cardHeader">
            <div className="DashboardPremium_cardTitle">Insights de localização</div>
            <button type="button" className="DashboardPremium_linkBtn" onClick={() => showMaintenance("Ver todos")}>
              Ver todos
            </button>
          </div>
          <div className="PremiumLocations_insights">
            <div className="PremiumLocations_insight">
              <span className="PremiumLocations_dot PremiumLocations_dotGreen"></span>
              <span>{resumo.destaque} está acima da média de faturamento por máquina.</span>
            </div>
            <div className="PremiumLocations_insight">
              <span className="PremiumLocations_dot PremiumLocations_dotOrange"></span>
              <span>Revise locais com máquinas offline para recuperar faturamento.</span>
            </div>
            <button type="button" className="PremiumLocations_cta" onClick={() => showMaintenance("Oportunidades de expansão")}>
              Ver oportunidades de expansão
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConfiguracoesPremium = () => {
  const [form, setForm] = useState({
    nomeEmpresa: "VendMachine Diversões",
    email: "contato@vendmachine.com.br",
    telefone: "(11) 98765-4321",
    moeda: "BRL",
    fuso: "America/Sao_Paulo",
    custoMedioPelucia: 8.5,
    taxaPagamento: 2.99,
    custosMensais: 450,
    metaLucroMensal: 25000,
    calcularLucroAuto: true,
    considerarCustosManutencao: true,
    alertaEstoqueBaixo: true,
    sugerirReposicao: false,
    bloquearPromocoesInativas: true,
    tema: "escuro",
    corPrincipal: "#7C3AED",
    exibirEm: "BRL",
    casasDecimais: 2,
    idioma: "pt-BR",
    backupAuto: true,
    freqBackup: "diario",
  });

  const showMaintenance = (label) => {
    Swal.fire({
      icon: "info",
      title: label,
      text: "Função em desenvolvimento.",
      confirmButtonText: "Ok",
    });
  };

  const onSave = () => {
    showMaintenance("Salvar alterações");
  };

  const toggle = (key) => {
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="PremiumSettings_container">
      <div className="PremiumSettings_header">
        <div>
          <div className="PremiumSettings_title">Configurações</div>
          <div className="PremiumSettings_sub">
            Personalize o sistema de acordo com as necessidades do seu negócio.
          </div>
        </div>
        <button type="button" className="PremiumSettings_saveBtn" onClick={onSave}>
          <i className="bi bi-check2"></i> Salvar alterações
        </button>
      </div>

      <div className="PremiumSettings_tabs">
        <button type="button" className="PremiumSettings_tab PremiumSettings_tabActive" onClick={() => showMaintenance("Geral")}>
          <i className="bi bi-gear"></i> Geral
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Máquinas")}>
          <i className="bi bi-hdd-stack"></i> Máquinas
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Financeiro")}>
          <i className="bi bi-cash-coin"></i> Financeiro
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Promoções")}>
          <i className="bi bi-megaphone"></i> Promoções
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Notificações")}>
          <i className="bi bi-bell"></i> Notificações
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Usuários")}>
          <i className="bi bi-people"></i> Usuários
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Integrações")}>
          <i className="bi bi-plug"></i> Integrações
        </button>
        <button type="button" className="PremiumSettings_tab" onClick={() => showMaintenance("Segurança")}>
          <i className="bi bi-shield-lock"></i> Segurança
        </button>
      </div>

      <div className="PremiumSettings_grid">
        <div className="PremiumSettings_card">
          <div className="PremiumSettings_cardTitle">Informações da empresa</div>
          <div className="PremiumSettings_formGrid">
            <label className="PremiumSettings_label">
              <span>Nome da empresa</span>
              <input className="PremiumSettings_input" value={form.nomeEmpresa} onChange={(e) => setForm((p) => ({ ...p, nomeEmpresa: e.target.value }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>E-mail</span>
              <input className="PremiumSettings_input" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Telefone</span>
              <input className="PremiumSettings_input" value={form.telefone} onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Moeda</span>
              <select className="PremiumSettings_input" value={form.moeda} onChange={(e) => setForm((p) => ({ ...p, moeda: e.target.value }))}>
                <option value="BRL">Real (R$)</option>
                <option value="USD">Dólar (US$)</option>
              </select>
            </label>
            <label className="PremiumSettings_label">
              <span>Fuso horário</span>
              <select className="PremiumSettings_input" value={form.fuso} onChange={(e) => setForm((p) => ({ ...p, fuso: e.target.value }))}>
                <option value="America/Sao_Paulo">(GMT-03:00) Brasília</option>
                <option value="America/Manaus">(GMT-04:00) Manaus</option>
              </select>
            </label>
          </div>
        </div>

        <div className="PremiumSettings_card">
          <div className="PremiumSettings_cardTitle">Configurações financeiras</div>
          <div className="PremiumSettings_formGrid">
            <label className="PremiumSettings_label">
              <span>Custo médio por pelúcia</span>
              <input className="PremiumSettings_input" type="number" value={form.custoMedioPelucia} onChange={(e) => setForm((p) => ({ ...p, custoMedioPelucia: Number(e.target.value) }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Taxa de pagamento (maquininhas)</span>
              <input className="PremiumSettings_input" type="number" value={form.taxaPagamento} onChange={(e) => setForm((p) => ({ ...p, taxaPagamento: Number(e.target.value) }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Outros custos mensais</span>
              <input className="PremiumSettings_input" type="number" value={form.custosMensais} onChange={(e) => setForm((p) => ({ ...p, custosMensais: Number(e.target.value) }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Meta de lucro mensal</span>
              <input className="PremiumSettings_input" type="number" value={form.metaLucroMensal} onChange={(e) => setForm((p) => ({ ...p, metaLucroMensal: Number(e.target.value) }))} />
            </label>
          </div>
        </div>

        <div className="PremiumSettings_card">
          <div className="PremiumSettings_cardTitle">Regras do sistema</div>
          <div className="PremiumSettings_switchRow" onClick={() => toggle("calcularLucroAuto")}>
            <div>
              <div className="PremiumSettings_switchTitle">Calcular lucro automaticamente</div>
              <div className="PremiumSettings_switchDesc">Ativar cálculo automático de lucro.</div>
            </div>
            <div className={`PremiumSettings_switch ${form.calcularLucroAuto ? "on" : ""}`}></div>
          </div>
          <div className="PremiumSettings_switchRow" onClick={() => toggle("considerarCustosManutencao")}>
            <div>
              <div className="PremiumSettings_switchTitle">Considerar custos de manutenção</div>
              <div className="PremiumSettings_switchDesc">Incluir custos de manutenção no cálculo do lucro.</div>
            </div>
            <div className={`PremiumSettings_switch ${form.considerarCustosManutencao ? "on" : ""}`}></div>
          </div>
          <div className="PremiumSettings_switchRow" onClick={() => toggle("alertaEstoqueBaixo")}>
            <div>
              <div className="PremiumSettings_switchTitle">Alerta de estoque baixo</div>
              <div className="PremiumSettings_switchDesc">Ativar alertas quando pelúcias estiverem abaixo do mínimo.</div>
            </div>
            <div className={`PremiumSettings_switch ${form.alertaEstoqueBaixo ? "on" : ""}`}></div>
          </div>
          <div className="PremiumSettings_switchRow" onClick={() => toggle("sugerirReposicao")}>
            <div>
              <div className="PremiumSettings_switchTitle">Sugerir reposição automática</div>
              <div className="PremiumSettings_switchDesc">Sugere reposição com base no giro.</div>
            </div>
            <div className={`PremiumSettings_switch ${form.sugerirReposicao ? "on" : ""}`}></div>
          </div>
          <div className="PremiumSettings_switchRow" onClick={() => toggle("bloquearPromocoesInativas")}>
            <div>
              <div className="PremiumSettings_switchTitle">Bloquear promoções inativas</div>
              <div className="PremiumSettings_switchDesc">Impede promoções expiradas/inativas.</div>
            </div>
            <div className={`PremiumSettings_switch ${form.bloquearPromocoesInativas ? "on" : ""}`}></div>
          </div>
        </div>

        <div className="PremiumSettings_card">
          <div className="PremiumSettings_cardTitle">Preferências de exibição</div>
          <div className="PremiumSettings_formGrid">
            <label className="PremiumSettings_label">
              <span>Tema</span>
              <select className="PremiumSettings_input" value={form.tema} onChange={(e) => setForm((p) => ({ ...p, tema: e.target.value }))}>
                <option value="escuro">Escuro</option>
                <option value="claro">Claro</option>
              </select>
            </label>
            <label className="PremiumSettings_label">
              <span>Cor principal</span>
              <input className="PremiumSettings_input" value={form.corPrincipal} onChange={(e) => setForm((p) => ({ ...p, corPrincipal: e.target.value }))} />
            </label>
            <label className="PremiumSettings_label">
              <span>Casas decimais</span>
              <select className="PremiumSettings_input" value={form.casasDecimais} onChange={(e) => setForm((p) => ({ ...p, casasDecimais: Number(e.target.value) }))}>
                <option value={2}>2 casas</option>
                <option value={0}>0 casas</option>
              </select>
            </label>
            <label className="PremiumSettings_label">
              <span>Idioma</span>
              <select className="PremiumSettings_input" value={form.idioma} onChange={(e) => setForm((p) => ({ ...p, idioma: e.target.value }))}>
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English</option>
              </select>
            </label>
          </div>
        </div>

        <div className="PremiumSettings_card PremiumSettings_full">
          <div className="PremiumSettings_cardTitle">Backup de dados</div>
          <div className="PremiumSettings_backupRow">
            <div className="PremiumSettings_switchRow" onClick={() => toggle("backupAuto")}>
              <div>
                <div className="PremiumSettings_switchTitle">Backup automático</div>
                <div className="PremiumSettings_switchDesc">Realizar backup automático dos dados.</div>
              </div>
              <div className={`PremiumSettings_switch ${form.backupAuto ? "on" : ""}`}></div>
            </div>
            <button type="button" className="PremiumSettings_backupBtn" onClick={() => showMaintenance("Backup manual")}>
              Fazer backup agora
            </button>
          </div>
        </div>

        <div className="PremiumSettings_card PremiumSettings_full PremiumSettings_danger">
          <div className="PremiumSettings_cardTitle">Zona de exclusão</div>
          <div className="PremiumSettings_dangerText">
            Excluir todos os dados do sistema permanentemente. Atenção: esta ação não pode ser desfeita!
          </div>
          <button type="button" className="PremiumSettings_dangerBtn" onClick={() => showMaintenance("Excluir todos os dados")}>
            Excluir todos os dados
          </button>
        </div>
      </div>
    </div>
  );
};
